// Demo:
// https://www.desmos.com/calculator/gmno7rlnvo

import { ITimeUnit } from './types';

export type IIndeterminateProgressOptions = {
  /**
   * The maximum progress to reach.
   *
   * @defaultValue 1
   */
  maxProgress?: number;

  /**
   * The maximum non-deterministic progress to reach. The progress will tend to
   * this value as time goes to infinity, until the progress become
   * deterministic when the process is completed.
   *
   * @defaultValue 0.8
   */
  maxNonDeterministicProgress?: number;

  /**
   * The expected progress to reach at the expected time. This is used to
   * calculate the coefficient of the non-deterministic progress.
   *
   * @defaultValue 0.75
   */
  expectedProgress?: number;

  /**
   * The expected time to reach the expected progress. This is used to calculate
   * the coefficient of the non-deterministic progress.
   *
   * @defaultValue 1000
   */
  atExpectedTime?: number;

  /**
   * The minimum duration to complete the progress. This is used to calculate
   * the deterministic progress.
   *
   * @defaultValue 250
   */
  minCompletionDuration?: number;

  /**
   * The maximum duration to complete the progress. This is used to calculate
   * the deterministic progress.
   *
   * @defaultValue 250
   */
  maxCompletionDuration?: number;
};

export type IIndeterminateProgressCompleteState = {
  dt: number;
  progress: number;
};

export class IndeterminateProgress {
  private readonly options;
  private readonly defaultOptions = {
    maxProgress: 1,
    maxNonDeterministicProgress: 0.8,
    expectedProgress: 0.6,
    atExpectedTime: 2000 * ITimeUnit.MILLISECOND,
    minCompletionDuration: 250,
    maxCompletionDuration: 250,
  };

  public constructor(options: IIndeterminateProgressOptions) {
    this.options = { ...this.defaultOptions, ...options };
  }

  private getCoeff(): number {
    const { expectedProgress, atExpectedTime, maxNonDeterministicProgress } =
      this.options;
    const coeff =
      -Math.log(1 - expectedProgress / maxNonDeterministicProgress) /
      atExpectedTime;

    return coeff;
  }

  private getTangent(
    dt: number,
    completeState: IIndeterminateProgressCompleteState,
  ): number {
    const { maxNonDeterministicProgress } = this.options;

    const a = this.getCoeff();
    const d = maxNonDeterministicProgress * a * Math.exp(-a * completeState.dt);
    const progress = this.getNonDeterministicProgress(dt) + d;

    return progress;
  }

  private getNonDeterministicProgress(dt: number): number {
    const { maxNonDeterministicProgress } = this.options;

    const a = this.getCoeff();
    const progress = maxNonDeterministicProgress * (1 - Math.exp(-a * dt));

    return progress;
  }

  private getDeterministicProgress(
    dt: number,
    completeState: IIndeterminateProgressCompleteState,
  ): number {
    const {
      maxProgress,
      maxNonDeterministicProgress,
      minCompletionDuration,
      maxCompletionDuration,
    } = this.options;

    const iCompletion = Math.max(
      minCompletionDuration,
      Math.min(maxCompletionDuration, completeState.dt),
    );
    const tCompleted = Math.max(completeState.dt, 1e-4);

    const a = this.getCoeff();
    const e = Math.exp(-a * completeState.dt);
    const tangent = this.getTangent(dt, completeState);
    const g = maxNonDeterministicProgress * a * e;
    const h = maxNonDeterministicProgress * (1 - e - completeState.dt * a * e);
    const c =
      (maxProgress - g * (tCompleted + iCompletion) - h) /
      (iCompletion / tCompleted) ** 2;
    const progress = tangent + c * ((dt - tCompleted) / tCompleted) ** 2;

    return progress;
  }

  public getProgress(
    dt: number,
    completeState?: IIndeterminateProgressCompleteState,
  ): number {
    return completeState
      ? Math.min(
          this.getDeterministicProgress(dt, completeState),
          this.options.maxProgress,
        )
      : this.getNonDeterministicProgress(dt);
  }
}
